import { ChevronRightIcon } from '@heroicons/react/24/solid';
import { useState } from 'react';
import { usePermission } from '../../../../hooks/usePermission';
import AppNavLink from '../../../Router/AppNavLink';
import type { NavItemInterface, SubNavItemInterface } from '..';

import styles from './NavItem.module.css';

export interface NavItemProps {
  item: NavItemInterface;
  isFullNavHidden?: boolean;
  onItemClick?: () => void;
  isSubMenuOpen?: boolean;
  onSubMenuToggle?: () => void;
  onCollapseExpanded?: () => void;
}

const rowBaseClassName = 'flex justify-center items-center p-2 text-sm hover:bg-neutralColor-lightest';

export default function NavItem({ item, isFullNavHidden, onItemClick, isSubMenuOpen, onSubMenuToggle, onCollapseExpanded }: NavItemProps) {
  const [localShowSubMenu, setLocalShowSubMenu] = useState(false);
  const { hasNavAccess } = usePermission();
  const isControlled = isSubMenuOpen !== undefined && onSubMenuToggle !== undefined;
  const showSubMenu = isControlled ? isSubMenuOpen : localShowSubMenu;

  const handleParentActivate = () => {
    if (item.subMenu) {
      if (isControlled) {
        onSubMenuToggle?.();
      } else {
        setLocalShowSubMenu((prevState) => !prevState);
      }

      if (showSubMenu && onItemClick) {
        onItemClick();
      }
      return;
    }

    if (isControlled) {
      onCollapseExpanded?.();
    }
  };

  const subMenuBubble =
    isFullNavHidden && item.subMenu ? (
      <div className={`${styles['item-bubble']} ${styles['hover-target']}`}>
        <span className={`block ${item.subMenu ? 'border-b' : ''} font-bold pb-1`}>{item.name}</span>
        {item.subMenu.map((sub: SubNavItemInterface) => {
          if (hasNavAccess(sub.permissions, sub.roles ?? item.roles, sub.requiresSystemAdmin ?? item.requiresSystemAdmin)) {
            return (
              <AppNavLink
                key={sub.name}
                to={item.path + sub.path}
                activeClassName='text-primaryColor-light'
                baseClassName='py-1.5 text-white hover:text-primaryColor-light flex items-center text-sm rounded-md'
              >
                {sub.name}
              </AppNavLink>
            );
          }
          return null;
        })}
      </div>
    ) : null;

  return (
    <div>
      {isFullNavHidden && item.subMenu ? (
        <div className={`relative ${styles['hover-trigger']}`}>
          <button
            type='button'
            className={`${rowBaseClassName} w-full border-0 bg-transparent cursor-pointer text-inherit`}
            onClick={handleParentActivate}
            aria-expanded={showSubMenu}
            aria-haspopup='true'
          >
            <item.icon className={`h-6 w-6 ${!isFullNavHidden && 'mr-3'}`} aria-hidden='true' />
            <span className={`w-80 overflow-hidden ${isFullNavHidden ? 'hidden' : 'block flex justify-between'}`}>
              {item.name}
              {item.subMenu && (
                <ChevronRightIcon
                  className={`h-5 w-5 transition duration-400 ease-in-out ${showSubMenu ? 'transform rotate-90' : 'transform rotate-0'}`}
                />
              )}
            </span>
          </button>
          {subMenuBubble}
        </div>
      ) : (
        <AppNavLink
          to={item.path}
          baseClassName={`${rowBaseClassName} ${styles['hover-trigger']}`}
          activeClassName={'bg-neutralColor-lightest text-primaryColor-normal'}
          inactiveClassName='text-neutralColor-normal hover:text-primaryColor-normal'
          clicked={handleParentActivate}
        >
          <item.icon className={`h-6 w-6 ${!isFullNavHidden && 'mr-3'}`} aria-hidden='true' />
          <span className={`w-80 overflow-hidden ${isFullNavHidden ? 'hidden' : 'block flex justify-between'}`}>
            {item.name}
            {item.subMenu && (
              <ChevronRightIcon
                className={`h-5 w-5 transition duration-400 ease-in-out ${showSubMenu ? 'transform rotate-90' : 'transform rotate-0'}`}
              />
            )}
          </span>
        </AppNavLink>
      )}

      <div className={`flex flex-col transition-height duration-500 ease-in-out ${isFullNavHidden ? 'hidden' : 'block'}`}>
        {item.subMenu &&
          showSubMenu &&
          item.subMenu.map((sub: SubNavItemInterface) => {
            if (hasNavAccess(sub.permissions, sub.roles ?? item.roles, sub.requiresSystemAdmin ?? item.requiresSystemAdmin)) {
              return (
                <AppNavLink
                  key={sub.name}
                  to={item.path + sub.path}
                  activeClassName='text-primaryColor'
                  inactiveClassName='text-neutralColor'
                  clicked={onItemClick}
                  baseClassName='px-4 py-2 ml-8 hover:text-primaryColor flex items-center text-sm'
                >
                  {sub.name}
                </AppNavLink>
              );
            }
            return null;
          })}
      </div>
    </div>
  );
}
